import React, { useContext, useEffect, useMemo } from 'react';

import { type CustomPostField, CustomPostFieldTypes } from 'common/api/resources/postFields';
import { CustomPostFieldsContext } from 'common/containers/CustomPostFieldsContainer';
import {
  AlphanumericConditions,
  DropdownConditions,
  InputTypes,
  MultiselectConditions,
  NumericConditions,
  getConditionInputType,
} from 'common/fieldFilters/utils';

import { findActiveOption } from '../util';

import type { PostCustomFieldFilter } from 'common/automations/constants';
import type { Option } from 'common/ui/common/select/SelectCommon';
import Input from 'common/uiV3/Input';
import { Select } from 'common/uiV3/Select';

interface Props {
  condition: PostCustomFieldFilter['condition'];
  value: PostCustomFieldFilter['value'];
  onChange: (
    condition?: PostCustomFieldFilter['condition'],
    value?: PostCustomFieldFilter['value'],
  ) => void;
  required: boolean;
}

const AlphanumericConditionOptions = [
  { label: 'Is empty', value: AlphanumericConditions.isEmpty },
  { label: 'Is not empty', value: AlphanumericConditions.isNotEmpty },
  { label: 'Contains', value: AlphanumericConditions.contains },
  { label: 'Does not contain', value: AlphanumericConditions.notContains },
];

const NumericConditionOptions = [
  { label: 'Equals', value: NumericConditions.equals },
  { label: 'Does Not Equal', value: NumericConditions.doesNotEqual },
  { label: 'Greater Than', value: NumericConditions.greaterThan },
  { label: 'Less Than', value: NumericConditions.lessThan },
];

const getConditionOptions = (activeField: CustomPostField | undefined) => {
  if (!activeField) {
    return [];
  }

  let conditionOptions: Option[] = [];
  switch (activeField.type) {
    case CustomPostFieldTypes.text:
    case CustomPostFieldTypes.multilineText:
      conditionOptions = AlphanumericConditionOptions;
      break;

    case CustomPostFieldTypes.integer:
      conditionOptions = NumericConditionOptions;
      break;

    case CustomPostFieldTypes.dropdown:
      conditionOptions = [{ label: 'is', value: DropdownConditions.is }];
      break;

    case CustomPostFieldTypes.multiselect:
      conditionOptions = [{ label: 'is one of', value: MultiselectConditions.isOneOf }];
      break;

    default:
      break;
  }
  return conditionOptions;
};

type ConditionInputProps = {
  inputType: InputTypes | null;
  onChange: (value: PostCustomFieldFilter['value']['value']) => void;
  required: boolean;
  value: PostCustomFieldFilter['value']['value'];
  valueOptions: Option[] | null;
};

const ConditionInput = ({
  inputType,
  onChange,
  required,
  value,
  valueOptions,
}: ConditionInputProps) => {
  // Set the default value to first option, if unset
  useEffect(() => {
    if (inputType !== null && valueOptions && valueOptions.length > 0 && !value) {
      onChange(valueOptions[0].value);
    }
  }, [inputType, valueOptions, value, onChange]);

  if (!inputType) {
    return null;
  }

  switch (inputType) {
    case InputTypes.text:
      return (
        <div className={required ? 'filterField3of3-required' : 'filterField3of3'}>
          <Input onChange={onChange} value={value} />
        </div>
      );
    case InputTypes.number:
      return (
        <div className={required ? 'filterField3of3-required' : 'filterField3of3'}>
          <Input onChange={onChange} value={value} type="number" />
        </div>
      );
    case InputTypes.dropdown:
      if (!valueOptions) {
        throw new Error('Implementation error: activeFieldOptions is required for dropdown input');
      }
      return (
        <div className={required ? 'filterField3of3-required' : 'filterField3of3'}>
          <Select
            onValueChange={(option) => onChange(option as string)}
            options={valueOptions}
            placeholder="Field value"
            value={findActiveOption(valueOptions, value)?.value}
            fullWidth
            isSearchable={valueOptions.length > 5}
          />
        </div>
      );
    default:
      return null;
  }
};

const PostCustomField = ({ required, condition, value, onChange }: Props) => {
  const customPostFields = useContext(CustomPostFieldsContext);

  const activeField = customPostFields?.data?.find((field) => field._id === value.fieldID);

  // Memo to prevent useEffect from continuously triggering
  const conditionOptions = useMemo(
    () => (activeField ? getConditionOptions(activeField) : []),
    [activeField],
  );
  const activeConditionOption = findActiveOption(conditionOptions, condition);

  // On init, select the first post field
  useEffect(() => {
    if (!value.fieldID && !customPostFields.loading && customPostFields?.data?.[0]) {
      onChange(condition, { fieldID: customPostFields.data[0]._id, value: '' });
    }
  }, [value, customPostFields, onChange, condition]);

  // If no condition selected, select the first valid one
  useEffect(() => {
    if (value.fieldID) {
      if (!activeConditionOption && conditionOptions.length > 0) {
        onChange(conditionOptions[0].value as PostCustomFieldFilter['condition'], value);
      }
    }
  }, [value, activeConditionOption, conditionOptions, onChange, condition]);

  if (customPostFields.loading || !customPostFields.data) {
    // Hide if loading fields
    return null;
  }

  if (customPostFields.data.length === 0) {
    return (
      <div className="filterField1of1">
        <Input readOnly value="No custom fields exist" />
      </div>
    );
  }

  // Generate the field options array
  let activeFieldOption: Option | undefined;
  const fieldOptions: Option[] = [];
  customPostFields.data.forEach((field) => {
    const fieldOption = {
      label: field.name,
      value: field._id,
    };
    if (field._id === value.fieldID) {
      activeFieldOption = fieldOption;
    }
    fieldOptions.push(fieldOption);
  });

  // Generate the value options array, if necessary
  let renderThirdField = false;
  let valueOptions = null;
  if (activeField && condition) {
    const inputType = getConditionInputType(condition);
    if (inputType) {
      renderThirdField = true;
    }
    if (inputType === InputTypes.dropdown && activeField.options) {
      valueOptions = activeField.options?.map((option) => ({
        label: option,
        value: option,
      }));
    }
  }

  return (
    <>
      <div className={renderThirdField ? 'filterField1of3' : 'filterField1of2'}>
        <Select
          placeholder="Post field"
          onValueChange={(option) =>
            option &&
            onChange(condition, { ...value, fieldID: option } as PostCustomFieldFilter['value'])
          }
          options={fieldOptions}
          value={activeFieldOption?.value}
          fullWidth
          isSearchable={fieldOptions.length > 5}
        />
      </div>
      {conditionOptions.length !== 1 ? (
        <div className={renderThirdField ? 'filterField2of3' : 'filterField2of2'}>
          <Select
            fullWidth
            onValueChange={(option) =>
              option &&
              onChange(option as PostCustomFieldFilter['condition'], { ...value, value: '' })
            }
            options={conditionOptions}
            value={activeConditionOption?.value}
            isSearchable={conditionOptions.length > 5}
          />
        </div>
      ) : (
        <div className="filterField2of3">
          <Input readOnly value={conditionOptions[0].label} />
        </div>
      )}
      {renderThirdField ? (
        <ConditionInput
          // TS isn't inferring the type of activeField properly
          inputType={getConditionInputType(condition)}
          onChange={(newValue) => onChange(condition, { ...value, value: newValue })}
          required={required}
          value={value.value}
          valueOptions={valueOptions}
        />
      ) : null}
    </>
  );
};
export default PostCustomField;
